/**
 * Movement intent helpers.
 *
 * Straight-line seeking, no pathfinding — the snowfield has no obstacles to
 * path around, only slope, and Lineage II's own client walked you into walls
 * anyway. One function drives the player's click destination and every mob's
 * chase, so they accelerate, turn and plant their feet identically.
 */

/** Distance at which a destination counts as reached. */
export const ARRIVE_RADIUS = 0.35;

/** Below this the seek eases off, so characters settle instead of jittering. */
const SLOW_RADIUS = 1.4;

/**
 * Point a controller at a world position.
 *
 * @param {import("../character/controller.js").CharacterController} ctrl
 * @param {number} destX
 * @param {number} destZ
 * @param {number} speed metres/second
 * @param {number} [stopAt] stop this far short of the destination
 * @returns {boolean} true once the destination is reached
 */
export function seek(ctrl, destX, destZ, speed, stopAt = ARRIVE_RADIUS) {
    const dx = destX - ctrl.position.x;
    const dz = destZ - ctrl.position.z;
    const dist = Math.hypot(dx, dz);

    if (dist <= stopAt) {
        halt(ctrl);
        return true;
    }

    const remaining = dist - stopAt;
    const it = ctrl.intent;
    it.wishX = dx / dist;
    it.wishZ = dz / dist;
    // Ease into the last metre and a half. Without this the walk cycle stutters
    // as the seek overshoots and reverses across the arrival radius.
    it.wishSpeed = remaining < SLOW_RADIUS
        ? Math.max(speed * 0.35, speed * (remaining / SLOW_RADIUS))
        : speed;
    return false;
}

/** Stop moving. Facing is left alone — a halted character keeps looking where it was. */
export function halt(ctrl) {
    const it = ctrl.intent;
    it.wishX = 0;
    it.wishZ = 0;
    it.wishSpeed = 0;
}

/** Yaw that points from a controller at a world position. */
export function angleTo(ctrl, x, z) {
    return Math.atan2(x - ctrl.position.x, z - ctrl.position.z);
}

/** Planar distance between a controller and a world position. */
export function distanceTo(ctrl, x, z) {
    return Math.hypot(x - ctrl.position.x, z - ctrl.position.z);
}
